import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
  HubConnectionState,
  IRetryPolicy,
  RetryContext,
} from "@microsoft/signalr";
import accountContext from "../../account/accountContext";

/**
 * Exponential backoff retry policy for the tracking SignalR connection.
 * Delays: 0, 2s, 4s, 8s, 16s, 30s, 30s, 30s, ... (capped at 30s)
 */
class TrackingRetryPolicy implements IRetryPolicy {
  private static readonly MAX_DELAY_MS = 30000;

  nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
    if (retryContext.previousRetryCount === 0) {
      return 0; // First retry is immediate
    }

    const delay = Math.min(
      1000 * Math.pow(2, retryContext.previousRetryCount),
      TrackingRetryPolicy.MAX_DELAY_MS
    );
    return delay;
  }
}

/**
 * Shared tracking SignalR connection reference.
 * Separate from the notifications connection — connects to /hubs/tracking.
 */
let trackingConnection: HubConnection | null = null;

export const createTrackingConnection = (): HubConnection | null => {
  const token = accountContext.accessToken;
  if (!token) {
    return null;
  }

  const baseUrl = (window as any).app.config.notificationsApiBaseUrl;
  if (!baseUrl) {
    return null;
  }

  const connection = new HubConnectionBuilder()
    .withUrl(`${baseUrl}/hubs/tracking`, {
      accessTokenFactory: () => accountContext.accessToken || "",
    })
    .withAutomaticReconnect(new TrackingRetryPolicy())
    .configureLogging(LogLevel.Warning)
    .build();

  trackingConnection = connection;
  return connection;
};

export const getTrackingConnection = (): HubConnection | null => {
  return trackingConnection;
};

export const stopTrackingConnection = async (): Promise<void> => {
  if (
    trackingConnection &&
    trackingConnection.state !== HubConnectionState.Disconnected
  ) {
    await trackingConnection.stop();
  }
  trackingConnection = null;
};
